/**
 * Axios API client — single boundary between UI and backend.
 *
 * Cookie-based sessions (ADR-005): access + refresh tokens live in httpOnly
 * cookies set by the backend. The browser sends them automatically because the
 * frontend and backend share the same origin, so we send `withCredentials` and
 * never read/store tokens in JS (XSS-safe). On a 401 we transparently rotate the
 * refresh cookie once and retry.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
	baseURL: API_BASE,
	headers: { 'Content-Type': 'application/json' },
	timeout: 15000,
	withCredentials: true,
});

let refreshing = null;

apiClient.interceptors.response.use(
	(res) => res,
	async (error) => {
		const original = error.config;
		const status = error.response?.status;
		const url = original?.url || '';
		const isAuthCall =
			url.includes('/auth/login') ||
			url.includes('/auth/refresh') ||
			url.includes('/auth/me');

		if (status === 401 && !original._retry && !isAuthCall) {
			original._retry = true;
			try {
				refreshing =
					refreshing ||
					axios.post(`${API_BASE}/v1/auth/refresh`, null, { withCredentials: true });
				await refreshing;
				refreshing = null;
				return apiClient(original);
			} catch (e) {
				refreshing = null;
			}
		}
		return Promise.reject(error);
	}
);

/** Optional explicit tenant header (public reads / super_admin tooling). */
export function setTenantHeader(municipalityId) {
	if (municipalityId) apiClient.defaults.headers.common['X-Municipality-Id'] = municipalityId;
	else delete apiClient.defaults.headers.common['X-Municipality-Id'];
}

/** Normalize FastAPI error detail (string | array | object) to a display string. */
export function formatApiError(detail) {
	if (detail == null) return 'Si è verificato un errore. Riprova.';
	if (typeof detail === 'string') return detail;
	if (Array.isArray(detail))
		return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
	if (detail && typeof detail.msg === 'string') return detail.msg;
	return String(detail);
}

export default apiClient;
